import React, { useState, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { useIntake } from '../../context/IntakeContext';
import { Button } from '../ui/Button';
import { CheckIcon, Edit2Icon, SendIcon, DownloadIcon, Loader2Icon, ShieldCheckIcon, AlertCircleIcon } from 'lucide-react';
import { MascotPanel } from '../ui/MascotPanel';
import { generateIntakeHTML } from '../../utils/generateIntakeHTML';
import { submitIntake } from '../../utils/submitIntake';
/** Detect iOS / iPadOS (including iPad in desktop mode) */
function isAppleDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPad in desktop mode reports as Mac but has touch support
  navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}
/**
 * Download or open the HTML intake so the user can save / print it as PDF.
 * – Desktop : opens a new window and triggers the browser print dialog.
 * – Apple   : opens the file as a data-URL in the current tab so iOS Safari
 *             shows the share sheet → "Print" or "Save to Files".
 */
function downloadOrPrintPDF(htmlContent: string, clientName: string) {
  if (isAppleDevice()) {
    // iOS Safari doesn't support window.print() in pop-ups.
    // Opening a data-URL shows the native share/print sheet.
    const encoded = encodeURIComponent(htmlContent);
    const dataUrl = `data:text/html;charset=utf-8,${encoded}`;
    window.location.href = dataUrl;
  } else {
    // Desktop & Android: open in new window and trigger print dialog.
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } else {
      // Pop-ups blocked – fall back to blob download.
      const blob = new Blob([htmlContent], {
        type: 'text/html'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `intake-noord-digital-${clientName.replace(/\s+/g, '-').toLowerCase()}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }
}
export function Step15Summary() {
  const {
    data,
    setCurrentStep
  } = useIntake();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const SummarySection = ({
    title,
    step,
    children
  }: {
    title: string;
    step: number;
    children: React.ReactNode;
  }) => <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6" data-id="element-189">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-190">
        <h3 className="font-bold text-gray-900" data-id="element-191">{title}</h3>
        <button onClick={() => setCurrentStep(step)} className="text-sm font-medium text-[#007AFF] hover:text-[#005FCC] flex items-center" data-id="element-192">
          <Edit2Icon className="w-4 h-4 mr-1" data-id="element-193" />
          Bewerken
        </button>
      </div>
      <div className="p-6 space-y-2" data-id="element-194">{children}</div>
    </div>;
  const DataRow = ({
    label,
    value
  }: {
    label: string;
    value: string | React.ReactNode;
  }) => <div className="flex flex-col sm:flex-row sm:py-2 border-b border-gray-100 last:border-0" data-id="element-195">
      <span className="text-sm font-medium text-gray-500 sm:w-1/3 mb-1 sm:mb-0" data-id="element-196">
        {label}
      </span>
      <span className="text-sm text-gray-900 sm:w-2/3 font-medium" data-id="element-197">
        {value || <span className="text-gray-400 italic" data-id="element-198">Niet ingevuld</span>}
      </span>
    </div>;
  const htmlContent = generateIntakeHTML(data);
  const clientName = data.companyName || data.contactName || 'Nieuwe Klant';
  const handleSubmit = async () => {
    if (!acceptedPrivacy) {
      alert('Ga akkoord met de privacyvoorwaarden om verder te gaan.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    const clientEmail = data.contactEmail || 'klant@voorbeeld.nl';
    const result = await submitIntake(htmlContent, clientEmail, clientName);
    setIsSubmitting(false);
    if (result.success) {
      setIsSuccess(true);
    } else {
      setSubmitError(result.error || 'Er is een fout opgetreden bij het verzenden. Probeer het opnieuw of neem contact op met info@noorddigital.nl.');
    }
  };
  const handleDownloadPDF = () => downloadOrPrintPDF(htmlContent, clientName);
  if (isSuccess) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="max-w-2xl mx-auto text-center py-12" data-id="element-199">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-200">
          <CheckIcon className="w-12 h-12 text-green-600" data-id="element-201" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-202">
          Intake Succesvol Verzonden!
        </h2>
        <p className="text-lg text-gray-600 mb-8" data-id="element-203">
          Bedankt voor uw tijd, {data.contactName}. We hebben uw gegevens in
          goede orde ontvangen op info@noorddigital.nl en een kopie naar{' '}
          {data.contactEmail} gestuurd. Wij nemen spoedig contact met u op.
        </p>

        <div className="bg-blue-50 p-8 rounded-3xl border border-blue-100" data-id="element-204">
          <h3 className="font-bold text-gray-900 mb-2" data-id="element-205">
            Bewaar een kopie voor uzelf
          </h3>
          <p className="text-sm text-gray-600 mb-6" data-id="element-206">
            U kunt uw volledige intake inclusief alle antwoorden downloaden als
            PDF voor uw eigen administratie.
          </p>
          <Button onClick={handleDownloadPDF} size="lg" className="shadow-md" data-id="element-207">
            <DownloadIcon className="w-5 h-5 mr-2" data-id="element-208" />
            {isAppleDevice() ? 'Intake Openen / Opslaan' : 'Download Intake als PDF'}
          </Button>
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="max-w-4xl mx-auto space-y-8" data-id="element-209">
      <MascotPanel message="Geweldig, we zijn er! Controleer hieronder uw gegevens. Als alles klopt, kunt u de intake definitief indienen. U kunt daarna alles downloaden als PDF." data-id="element-210" />

      {submitError && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-xl" data-id="element-211">
          <div className="flex items-start" data-id="element-212">
            <AlertCircleIcon className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-213" />
            <div data-id="element-214">
              <h3 className="text-sm font-semibold text-red-800 mb-1" data-id="element-215">
                Verzenden mislukt
              </h3>
              <p className="text-sm text-red-700" data-id="element-216">{submitError}</p>
              <p className="text-sm text-red-700 mt-2" data-id="element-217">
                U kunt de intake ook downloaden als PDF en handmatig opsturen
                naar{' '}
                <a href="mailto:info@noorddigital.nl" className="underline font-semibold" data-id="element-218">
                  info@noorddigital.nl
                </a>
              </p>
            </div>
          </div>
        </motion.div>}

      <div className="space-y-6" data-id="element-219">
        <SummarySection title="Contact & Bedrijf" step={2} data-id="element-220">
          <DataRow label="Naam" value={data.contactName} data-id="element-221" />
          <DataRow label="Functie" value={data.contactRole} data-id="element-222" />
          <DataRow label="E-mail" value={data.contactEmail} data-id="element-223" />
          <DataRow label="Telefoon" value={data.contactPhone} data-id="element-224" />
          <DataRow label="Bedrijfsnaam" value={data.companyName} data-id="element-225" />
          <DataRow label="KVK Nummer" value={data.cocNumber} data-id="element-226" />
          {data.multipleLocations === 'yes' ? <DataRow label="Aantal locaties" value={`${data.locations.length} locaties opgegeven`} data-id="element-227" /> : <DataRow label="Adres" value={`${data.mainStreet}, ${data.mainCity}`} data-id="element-228" />}
        </SummarySection>

        <SummarySection title="Verhaal & Branding" step={5} data-id="element-229">
          <DataRow label="Diensten" value={data.services.length > 0 ? data.services.map(s => s.name).filter(Boolean).join(', ') || `${data.services.length} dienst(en) opgegeven` : ''} data-id="element-230" />
          <DataRow label="Kernwaarden" value={data.coreValues} data-id="element-231" />
          <DataRow label="Ideale website" value={data.idealWebsiteDescription} data-id="element-232" />
          <DataRow label="Huisstijl aanwezig" value={data.hasIdentity === 'yes_complete' ? 'Ja, compleet' : data.hasIdentity === 'yes_partly' ? 'Ja, deels' : 'Nee'} data-id="element-233" />
          <DataRow label="Typografie wens" value={data.typographyChoice} data-id="element-234" />
          <DataRow label="Merkkleuren" value={data.preferredColors.length > 0 ? data.preferredColors.map(c => c.name).join(', ') : ''} data-id="element-235" />
        </SummarySection>

        <SummarySection title="Website Doel & Structuur" step={8} data-id="element-236">
          <DataRow label="Primair doel" value={data.mainGoal} data-id="element-237" />
          <DataRow label="Type website" value={data.websiteType} data-id="element-238" />
          <DataRow label="Inspiratie websites" value={data.inspirationWebsites} data-id="element-239" />
          <DataRow label="Gewenste indruk" value={data.desiredImpression} data-id="element-240" />
          <DataRow label="Gewenste pagina's" value={[...data.neededPages, ...data.customPages].filter(p => p !== 'other').join(', ')} data-id="element-241" />
          <DataRow label="Content aanlevering" value={data.existingContent[0]} data-id="element-242" />
        </SummarySection>

        <SummarySection title="Techniek, SEO & Planning" step={12} data-id="element-243">
          <DataRow label="Domeinnaam" value={data.domainOwned === 'yes' ? data.domainName : 'Moet nog geregistreerd worden'} data-id="element-244" />
          <DataRow label="Hosting" value={data.hostingActive === 'noord' ? 'Via Noord Digital' : 'Zelf regelen'} data-id="element-245" />
          <DataRow label="Toon (Tone of Voice)" value={data.toneOfVoice === 'formal' ? 'Formeel' : data.toneOfVoice === 'informal' ? 'Informeel' : data.toneOfVoice === 'mix' ? 'Mix' : data.toneOfVoice === 'advise' ? 'Adviseer mij' : ''} data-id="element-246" />
          <DataRow label="Belang van SEO" value={data.seoServices} data-id="element-247" />
          <DataRow label="SEO Silo pakket" value={data.seoSiloPackage === 'silver' ? 'Zilver' : data.seoSiloPackage === 'gold' ? 'Goud' : data.seoSiloPackage === 'platinum' ? 'Platinum' : ''} data-id="element-248" />
          <DataRow label="Silo thema's" value={data.seoSiloThemes} data-id="element-249" />
          <DataRow label="Silo regio's" value={data.seoSiloRegions} data-id="element-250" />
          <DataRow label="Gewenste live datum" value={data.launchDate} data-id="element-251" />
        </SummarySection>
      </div>

      <div className="bg-white rounded-3xl p-8 border border-gray-200 mt-12 shadow-sm" data-id="element-252">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-253">
          Klaar om te verzenden?
        </h2>

        <div className="max-w-xl mx-auto mb-8" data-id="element-254">
          <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors" data-id="element-255">
            <div className="flex-shrink-0 mt-0.5" data-id="element-256">
              <input type="checkbox" className="w-5 h-5 rounded border-gray-300 text-[#007AFF] focus:ring-[#007AFF]" checked={acceptedPrivacy} onChange={e => setAcceptedPrivacy(e.target.checked)} data-id="element-257" />
            </div>
            <div data-id="element-258">
              <p className="text-sm font-medium text-gray-900 flex items-center" data-id="element-259">
                <ShieldCheckIcon className="w-4 h-4 mr-1 text-green-600" data-id="element-260" />
                Privacy & AVG Akkoord
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-261">
                Ik ga ermee akkoord dat Noord Digital mijn ingevulde gegevens
                verwerkt en opslaat conform de AVG wetgeving om contact op te
                nemen over dit project. De gegevens worden veilig verstuurd.
              </p>
            </div>
          </label>
        </div>

        <div className="text-center space-y-4" data-id="element-262">
          <Button size="lg" onClick={handleSubmit} disabled={!acceptedPrivacy || isSubmitting} className="px-12 py-4 text-lg shadow-xl w-full sm:w-auto" data-id="element-263">
            {isSubmitting ? <>
                <Loader2Icon className="w-5 h-5 mr-2 animate-spin" data-id="element-264" />
                Bezig met verzenden...
              </> : <>
                <SendIcon className="w-5 h-5 mr-2" data-id="element-265" />
                Intake Definitief Verzenden
              </>}
          </Button>

          <div className="flex items-center justify-center gap-2" data-id="element-266">
            <span className="text-xs text-gray-400" data-id="element-267">of</span>
          </div>

          <Button variant="outline" size="lg" onClick={handleDownloadPDF} className="w-full sm:w-auto" data-id="element-268">
            <DownloadIcon className="w-5 h-5 mr-2" data-id="element-269" />
            {isAppleDevice() ? 'Intake Openen / Opslaan als PDF' : 'Download als PDF'}
          </Button>

          <p className="text-xs text-gray-400 mt-4" data-id="element-270">
            Na het verzenden kunt u de volledige intake ook downloaden als PDF.
          </p>
        </div>
      </div>
    </motion.div>;
}